var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx } from "react/jsx-runtime";
import { bool, number, string } from "yup";
import { PaymentProcessor } from "../Account/BillingInformation/Shared/types/PaymentProcessorTypes";
import { shapeDeliveryAnswers } from "../Addresses/addressesUtils";
import { OrderFlowErrorMessages } from "../OrderFlow/hooks/Session/useSession";
import { ModalContent } from "../Shared/components/Modal";
import { LogLevel } from "../Shared/hooks/useLogging/Logging-types";
import { axiosPrivate } from "../api/axios";
import { CheckoutText, } from "./Checkout-types";
export const CHECKOUT_FORM_ID = "checkout-form";
/**
 * Returns the address whose zip matches the one on the order
 * @param zip
 * @param customerAddresses
 * @returns
 */
export const getAddressForZip = (zip, customerAddresses) => {
    if (!zip || !customerAddresses)
        return;
    return customerAddresses.filter((address) => address.zip === zip);
};
/**
 * Returns the address that the customer has flagged as their primary address
 * @param customerAddresses
 * @returns
 */
export const getPrimaryAddress = (customerAddresses) => {
    if (!customerAddresses)
        return;
    return customerAddresses.find((address) => address.isPrimary);
};
/**
 * Fetch all required info for checkout
 * @param sku
 * @param orderInfo
 * @returns
 */
export const getCheckoutInfo = (sku, datadogLog, orderInfo) => __awaiter(void 0, void 0, void 0, function* () {
    var _a;
    try {
        const [checkoutInfoRes, customerInfoRes, configurableTextRes] = yield Promise.all([
            axiosPrivate.post(`/GetCheckoutData`, orderInfo !== null && orderInfo !== void 0 ? orderInfo : {}, {
                params: {
                    sku,
                },
            }),
            axiosPrivate.get("/GetCustomerInfo"),
            axiosPrivate.get(`/GetCheckoutConfigurableText`, { params: { sku: sku } }),
        ]);
        const { data: { pickUpLocations, success, responseMsg, termsAndConditionsText, termsAndConditionsTitle, deliverySectionTitle, deliverySectionText, questionSectionText, questionSectionTitle, pickupQuestions, orderInfo: orderInfoFromResponse, coolerChargeText, skipPayment, validEmail, emailErrorText, enableGiftCards, overlappingStatus, canPayLater, specialInstructions, payLaterText, }, } = checkoutInfoRes;
        const { data: { customerShippingAddresses, primaryShippingAddress }, } = customerInfoRes;
        const { data: { checkoutConfigurableText }, } = configurableTextRes;
        const data = {
            customerShippingAddresses,
            pickUpLocations,
            primaryShippingAddress,
            success,
            responseMsg,
            termsAndConditionsText,
            termsAndConditionsTitle,
            deliverySectionTitle,
            deliverySectionText,
            questionSectionText,
            questionSectionTitle,
            pickupQuestions,
            orderInfo: orderInfoFromResponse,
            coolerChargeText,
            skipPayment,
            validEmail,
            emailErrorText,
            enableGiftCards,
            checkoutConfigurableText,
            overlappingStatus,
            canPayLater,
            specialInstructions,
            payLaterText,
        };
        return data;
    }
    catch (err) {
        datadogLog((_a = err.message) !== null && _a !== void 0 ? _a : "Error fetching checkout info", LogLevel.ERROR, undefined, err);
        console.error(err);
    }
});
/**
 * Display error toast if order is delivery and address not chosen
 * @param isLocationSelected
 * @param isAddressSelected
 * @param toast
 * @returns
 */
export const checkForDeliveryAddressLocationSelection = (isLocationSelected, isAddressSelected, toast) => {
    if (!isLocationSelected && !isAddressSelected) {
        toast.push(CheckoutText.PLEASE_SELECT_ADDRESS, { variant: "error" });
    }
};
/**
 * POST to confirm customer is eligible to checkout
 * @param flowSku
 * @param orderInfo
 * @returns
 */
export const confirmCheckoutEligibility = (flowSku, checkoutModel) => {
    return axiosPrivate.post(`/ConfirmCheckOutEligibility`, checkoutModel, {
        params: {
            flowSku,
        },
    });
};
/**
 * Completes checkout using either Stripe or Authorize
 * @param setLoading
 * @param flowSku
 * @param toast
 * @param navigate
 * @param showModal
 * @param resetCheckoutContext
 * @param resetBillingInfoContext
 * @param isMobileOrder
 * @param paymentProcessor
 * @param paymentIntentId
 * @param orderInfo
 * @param addresses
 * @param questions
 * @param mobileRedirectSuccessUrl
 * @param mobileRedirectFailureUrl
 * @returns
 */
export const checkoutFormSubmit = (setLoading, flowSku, toast, navigate, showModal, resetCheckoutContext, resetBillingInfoContext, resetOrderFlowContext, revalidatePaymentMethods, goBackToOrderFlow, isMobileOrder, paymentMethodFieldValue, initPayWithPaymentMethod, isPayLaterSelected, paymentProcessor, paymentIntentId, orderInfo, addresses, questions, mobileRedirectSuccessUrl, mobileRedirectFailureUrl) => (data) => __awaiter(void 0, void 0, void 0, function* () {
    var _b;
    setLoading(true);
    const address = addresses === null || addresses === void 0 ? void 0 : addresses.find((a) => a.addressId === data.address);
    const deliveryAnswers = questions ? shapeDeliveryAnswers(data, questions) : undefined;
    let checkoutInfo = {
        ShippingFirstName: address === null || address === void 0 ? void 0 : address.firstName,
        ShippingLastName: address === null || address === void 0 ? void 0 : address.lastName,
        ShippingStreetAddress: address === null || address === void 0 ? void 0 : address.address1,
        ShippingStreetAddress2: address === null || address === void 0 ? void 0 : address.address2,
        ShippingCity: address === null || address === void 0 ? void 0 : address.city,
        ShippingState: address === null || address === void 0 ? void 0 : address.state,
        ShippingZip: address === null || address === void 0 ? void 0 : address.zip,
        PaymentMethodId: data.paymentMethod,
        DeliveryAnswers: deliveryAnswers,
    };
    const checkoutModel = {
        orderInfo: orderInfo,
        checkoutInfo: checkoutInfo,
        skipPayment: isPayLaterSelected,
    };
    const res = yield confirmCheckoutEligibility(flowSku, checkoutModel);
    const { data: { success, skipPayment }, } = res;
    if (!success) {
        goBackToOrderFlow(flowSku, (_b = res.data.responseMsg) !== null && _b !== void 0 ? _b : OrderFlowErrorMessages.PROCESSING_ORDER);
        return;
    }
    checkout(setLoading, toast, navigate, showModal, resetCheckoutContext, resetBillingInfoContext, resetOrderFlowContext, revalidatePaymentMethods, flowSku, isMobileOrder, skipPayment, paymentMethodFieldValue, initPayWithPaymentMethod, paymentProcessor, checkoutModel, paymentIntentId, data.paymentMethod.toString(), mobileRedirectSuccessUrl, mobileRedirectFailureUrl);
});
export const checkout = (setLoading, toast, navigate, showModal, resetCheckoutContext, resetBillingInfoContext, resetOrderFlowContext, revalidatePaymentMethods, flowSku, isMobileOrder, skipPayment, paymentMethodFieldValue, initPayWithPaymentMethod, paymentProcessor, checkoutModel, paymentIntentId, creditCardId, mobileRedirectSuccessUrl, mobileRedirectFailureUrl) => __awaiter(void 0, void 0, void 0, function* () {
    var _c, _d, _e, _f;
    const res = yield axiosPrivate.post(`/Checkout`, {
        orderInfo: checkoutModel.orderInfo,
        giftCardInfo: checkoutModel.giftCardInfo,
        checkoutInfo: checkoutModel.checkoutInfo,
        paymentIntentId: paymentIntentId,
        paymentProcessor: paymentProcessor,
        skipPayment: skipPayment,
    }, { params: { sku: flowSku, isMobileOrder } });
    // orderInfo will only have a total at Order Flow checkout.
    const paymentAmount = (_f = (_d = (_c = checkoutModel.orderInfo) === null || _c === void 0 ? void 0 : _c.total) !== null && _d !== void 0 ? _d : (_e = checkoutModel.giftCardInfo) === null || _e === void 0 ? void 0 : _e.paymentAmount) !== null && _f !== void 0 ? _f : 0;
    if (res.data.success) {
        if (!!res.data.extraStep) {
            const paymentProcessorString = PaymentProcessor[paymentProcessor].toLowerCase();
            const paymentProcessorResponse = res.data[paymentProcessorString];
            showModal({
                title: "Payment Confirmation",
                content: (_jsx(ModalContent, { children: _jsx("iframe", { style: { height: "75vh", overflow: "scroll", border: "none", width: "100%" }, src: paymentProcessorResponse.redirectToUri }) })),
                closeModalFunc: () => {
                    confirmThenCheckout(setLoading, toast, navigate, resetCheckoutContext, resetBillingInfoContext, resetOrderFlowContext, revalidatePaymentMethods, flowSku, isMobileOrder, paymentMethodFieldValue, initPayWithPaymentMethod, paymentProcessor, checkoutModel, paymentProcessorResponse.chargeId, paymentProcessorResponse.paymentIntentId, creditCardId, mobileRedirectSuccessUrl, mobileRedirectFailureUrl);
                },
                maxWidth: "sm",
                fullWidth: true,
            });
        }
        else {
            successRedirect(navigate, flowSku, resetCheckoutContext, resetBillingInfoContext, resetOrderFlowContext, revalidatePaymentMethods, checkoutModel, mobileRedirectSuccessUrl);
        }
    }
    else {
        failureRedirect(toast, setLoading, paymentAmount, paymentMethodFieldValue, initPayWithPaymentMethod, res.data, mobileRedirectFailureUrl);
    }
});
const confirmThenCheckout = (setLoading, toast, navigate, resetCheckoutContext, resetBillingInfoContext, resetOrderFlowContext, revalidatePaymentMethods, flowSku, isMobileOrder, paymentMethodFieldValue, initPayWithPaymentMethod, paymentProcessor, checkoutModel, chargeId, paymentIntentId, creditCardId, mobileRedirectSuccessUrl, mobileRedirectFailureUrl) => __awaiter(void 0, void 0, void 0, function* () {
    var _g, _h, _j, _k;
    // orderInfo will only have a total at Order Flow checkout.
    const paymentAmount = (_k = (_h = (_g = checkoutModel.orderInfo) === null || _g === void 0 ? void 0 : _g.total) !== null && _h !== void 0 ? _h : (_j = checkoutModel.giftCardInfo) === null || _j === void 0 ? void 0 : _j.paymentAmount) !== null && _k !== void 0 ? _k : 0;
    const res = yield axiosPrivate.post(`/ConfirmThenCheckout`, {
        orderInfo: checkoutModel.orderInfo,
        giftCardInfo: checkoutModel.giftCardInfo,
        checkoutInfo: checkoutModel.checkoutInfo,
        paymentIntentId: paymentIntentId,
        paymentProcessor: paymentProcessor,
    }, {
        params: {
            sku: flowSku,
            isMobileOrder,
            paymentMethodId: creditCardId,
            amount: paymentAmount,
            paymentProcessor: paymentProcessor,
            paymentIntentId: paymentIntentId,
            chargeId: chargeId,
        },
    });
    if (res.data.success) {
        successRedirect(navigate, flowSku, resetCheckoutContext, resetBillingInfoContext, resetOrderFlowContext, revalidatePaymentMethods, checkoutModel, mobileRedirectSuccessUrl);
    }
    else {
        failureRedirect(toast, setLoading, paymentAmount, paymentMethodFieldValue, initPayWithPaymentMethod, res.data, mobileRedirectFailureUrl);
    }
});
/**
 * Redirect to Thank You page unless we're in the SFF mobile up then redirect to the supplied URL
 * @param navigate
 * @param flowSku
 * @param orderInfo
 * @param mobileRedirectSuccessUrl
 */
const successRedirect = (navigate, flowSku, resetCheckoutContext, resetBillingInfoContext, resetOrderFlowContext, revalidatePaymentMethods, checkoutModel, mobileRedirectSuccessUrl) => {
    if (mobileRedirectSuccessUrl) {
        window.location.href = mobileRedirectSuccessUrl;
    }
    else {
        navigate(`/${flowSku}/ThankYou`, {
            replace: true,
            state: { orderInfo: checkoutModel.orderInfo, giftCard: checkoutModel.giftCardInfo },
        });
        resetCheckoutContext();
        resetBillingInfoContext(); // Clears UI state (tokens, intents)
        resetOrderFlowContext();
        revalidatePaymentMethods(); // Ensure SWR cache is fresh after checkout
    }
};
/**
 * Display error toast unless we're in the SFF mobile up then redirect to the supplied URL
 * @param setLoading
 * @param toast
 * @param errorMessage
 * @param mobileRedirectFailureUrl
 */
const failureRedirect = (toast, setLoading, paymentAmount, paymentMethodFieldValue, initPayWithPaymentMethod, response, mobileRedirectFailureUrl) => {
    var _a, _b;
    if (mobileRedirectFailureUrl) {
        window.location.href = mobileRedirectFailureUrl;
    }
    if (response.restartPayFlow) {
        toast.push((_a = response === null || response === void 0 ? void 0 : response.responseMsg) !== null && _a !== void 0 ? _a : CheckoutText.SOMETHING_WENT_WRONG_PAYMENT, { variant: "warning" });
        initPayWithPaymentMethod({
            PaymentMethodId: paymentMethodFieldValue,
            Amount: paymentAmount,
        });
        setLoading(false);
    }
    else {
        toast.push((_b = response === null || response === void 0 ? void 0 : response.responseMsg) !== null && _b !== void 0 ? _b : CheckoutText.SOMETHING_WENT_WRONG_GENERIC, { variant: "error" });
        setLoading(false);
    }
};
/**
 * Conditionally build schema for checkout form
 * @param shouldShowTermsCheckbox
 * @param isDeliveryOrder
 * @param skipPayment
 * @returns
 */
export const buildCheckoutFormSchema = (shouldShowTermsCheckbox, isDeliveryOrder, skipPayment) => {
    let schema = {
        acceptedTerms: bool().optional(),
        address: number().optional(),
        location: string().optional(),
        paymentMethod: string().optional(),
    };
    if (shouldShowTermsCheckbox) {
        schema.acceptedTerms = bool().oneOf([true], CheckoutText.PLEASE_ACCEPT_TERMS);
    }
    if (isDeliveryOrder) {
        schema.address = number().required(CheckoutText.PLEASE_SELECT_ADDRESS);
    }
    if (!isDeliveryOrder) {
        schema.location = string().required(CheckoutText.PLEASE_SELECT_LOCATION);
    }
    if (!skipPayment) {
        schema.paymentMethod = string().required(CheckoutText.PLEASE_SELECT_PAYMENT);
    }
    return schema;
};
